<template>
  <div
    v-if="view === 'redirect'"
    class="market-switcher"
  >
    <div class="market-switcher__header">
      <h2
        class="text-heading-xl text-heading-3xl-desktop"
        v-text="$string(
          'general.market_switcher.heading',
          {
            replace: { country: selectedCountry.name }
          }
        )"
      />

      <p
        class="text-body-m"
        v-text="$string(
          'general.market_switcher.subheading',
          {
            replace: {
              currency: `${selectedCountry.currency.code} ${selectedCountry.currency.symbol}`
            }
          }
        )"
      />
    </div>

    <div class="market-switcher__controls">
      <btn
        class="market-switcher__button"
        :label="$string('general.market_switcher.continue')"
        modifiers="boxed"
        :show-icon="false"
        type="button"
        @click="handleConfirm"
      />

      <btn
        class="market-switcher__button"
        :label="$string('general.market_switcher.change')"
        modifiers="outlined"
        :show-icon="false"
        type="button"
        @click="view = 'switcher'"
      />
    </div>
  </div>

  <div
    v-else
    class="market-switcher"
  >
    <h2
      class="
        market-switcher__header
        text-heading-xl
        text-heading-3xl-desktop
      "
      v-text="$string('general.market_switcher.switcher_heading')"
    />

    <form
      method="post"
      action="/localization"
      class="market-switcher__form"
      @submit.prevent="onSubmit"
    >
      <input
        type="hidden"
        name="_method"
        value="put"
      >

      <div class="market-switcher__form-group form-group">
        <div class="form-group__field">
          <label
            class="form-group__label text-body-m"
            for="market-switcher-name"
            v-text="$string('general.market_switcher.switcher_select_label')"
          />

          <select
            id="market-switcher-name"
            class="text-body-s"
            name="country_code"
          >
            <option
              v-for="listCountry of localization.countries"
              :key="listCountry.code"
              :value="listCountry.code"
              :selected="getCountrySelectedState(listCountry)"
              v-html="getCountryLabel(listCountry)"
            />
          </select>
        </div>
      </div>

      <btn
        class="market-switcher__button"
        :label="$string('general.market_switcher.switcher_continue')"
        modifiers="boxed"
        :show-icon="false"
        type="submit"
      />

      <btn
        class="market-switcher__button link"
        :label="$string('general.market_switcher.switcher_cancel')"
        modifiers="text"
        :show-icon="false"
        type="button"
        @click="view = 'redirect'"
      />
    </form>
  </div>
</template>

<script>

/**
 * Vue: Market Switcher (market-switcher)
 * -----------------------------------------------------------------------------
 * An overlay which allows the customer to change their country preference.
 *
 * @param {Boolean} isActive - The overlay active state.
 * @param {Object} localization - Base localization settings.
 * @param {Object} country - Base country information.
 *
 */
import { mapActions, mapState } from 'vuex'
import Btn from '~global/btn/btn'

export default {
  name: 'MarketSwitcher',

  components: {
    Btn,
  },

  props: {
    isActive: {
      type: Boolean,
      default: false,
    },

    localLocalization: {
      type: Object,
      default: () => ({
        countries: [],
      }),
    },

    country: {
      type: Object,
      default: () => ({
        name: '',
        code: '',
        currency: {
          code: '',
          symbol: '',
        },
      }),
    },
  },

  data() {
    return {
      view: 'redirect',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      geolocation: (state) => state['market-switcher'].geolocation,
      localization: (state) => state['market-switcher'].localization,
      selectedCountry: (state) => state['market-switcher'].country,
    }),
  },

  watch: {

    /**
     * Watches for the overlay active state to change.
     * - If it isn't active, trigger to confirm action.
     */
    isActive() {
      if (this.isActive) {
        return
      }

      this.handleConfirm()
    },
  },

  async mounted() {
    await this.queryGeolocation()

    this.setLocalization(this.localLocalization)
    this.setCountry(this.country)
    this.marketChecks()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      setGeolocation: 'market-switcher/setGeolocation',
      setLocalization: 'market-switcher/setLocalization',
      setCountry: 'market-switcher/setCountry',
    }),

    /**
     * Query geolocation data from Shopify and handle it.
     */
    async queryGeolocation() {

      try {
        const query = await fetch('/browsing_context_suggestions.json')
          .then((response) => response.json())

        this.setGeolocation(query)

      } catch (error) {
        throw new Error('Failed to fetch geolocation data', error)
      }
    },

    /**
      * Runs tests to decide if the overlay should show.
      * - If the dismissed state has expired and the user's country is different
      * to the active country, show the overlay.
     */
    marketChecks() {
      const expiryDate = localStorage.getItem('market-switcher-modal-dismissed-date')
      const now = new Date().getTime()

      if (
        ((expiryDate && now > expiryDate) || !expiryDate) &&
        this.country.code !== this.geolocation.code
      ) {
        localStorage.removeItem('market-switcher-modal-dismissed-date')

        this.openOverlay({
          component: 'market-switcher',
          ignoreDismissed: true,
          namespace: 'marketSwitcher',
        })
      }
    },

    /**
     * Handles the user's confirm action.
     * - Stores the dismissed state for 14 days.
     */
    handleConfirm() {
      const expiry = new Date(Date.now() + 12096e5)

      window.localStorage.setItem(
        'market-switcher-modal-dismissed-date',
        expiry,
      )

      this.closeOverlay('marketSwitcher')
    },

    /**
     * Handles the form submit event.
     * - Confirms the user action then submits.
     *
     * @param {Object} event - The event object.
     */
    onSubmit(event) {
      const form = event.target

      this.handleConfirm()
      form.submit()
    },

    /**
     * Returns the label for a given country.
     * @param {Object} country - The country object.
     * @returns {String}
     */
    getCountryLabel(country) {
      return `${country.name} (${country.currency.code} ${country.currency.symbol})`
    },

    /**
     * Returns the selected state for a given country.
     * @param {Object} country - The country object.
     * @returns {Boolean}
     */
    getCountrySelectedState(country) {
      return this.country.code === country.code
    },
  },
}
</script>

<style lang="scss">
@import './market-switcher';
</style>
